import React from 'react';
import {Box, Chip, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from "@mui/material"
import {alpha} from "@mui/material/styles"
import {ClassifierMetricsJson} from "../../api/json/ClassifierMetricsJson"
import {PredefinedOptionJson} from "../../api/json/PredefinedOptionJson"
import {resolveClassColor} from "../../library/ClassColors"
import {ClassifierFilter, hasActiveClassifierFilter, LOW_CONFIDENCE_THRESHOLD} from "../../library/ClassifierFilter"

interface ClassifierMetricsPanelProps {
    metrics: ClassifierMetricsJson
    classOptions?: PredefinedOptionJson[]
    filter: ClassifierFilter
    onFilterChange: (filter: ClassifierFilter) => void
}

const OTHER_COLUMN = "Other"

function formatPercent(value: number): string {
    return `${Math.round(value * 100)}%`
}

const headerCellSx = {textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, color: "text.secondary"} as const

const dotSx = (color: string) => ({
    display: "inline-block",
    width: 8,
    height: 8,
    borderRadius: "50%",
    bgcolor: color,
    mr: 0.75,
    verticalAlign: "baseline",
})

function ClassifierMetricsPanel({metrics, classOptions, filter, onFilterChange}: ClassifierMetricsPanelProps) {
    const {classes, rows, hasUnmatched} = metrics
    const rowByClass = new Map(rows.map(row => [row.expectedClass, row]))

    const drilldownEquals = (expected?: string, predicted?: string, unmatched?: boolean) =>
        filter.expectedClass === expected
        && filter.predictedClass === predicted
        && Boolean(filter.unmatchedOnly) === Boolean(unmatched)

    const toggleDrilldown = (expected?: string, predicted?: string, unmatched?: boolean) => {
        if (drilldownEquals(expected, predicted, unmatched)) {
            onFilterChange({...filter, expectedClass: undefined, predictedClass: undefined, unmatchedOnly: undefined})
        } else {
            onFilterChange({...filter, expectedClass: expected, predictedClass: predicted, unmatchedOnly: unmatched || undefined})
        }
    }

    const cellSx = (count: number, isDiagonal: boolean, selected: boolean) => ({
        bgcolor: count === 0
            ? "transparent"
            : (theme: any) => alpha(isDiagonal ? theme.palette.success.main : theme.palette.error.main, 0.14),
        color: count === 0 ? "text.disabled" : "text.primary",
        fontWeight: isDiagonal && count > 0 ? 600 : 400,
        cursor: count > 0 ? "pointer" : "default",
        boxShadow: selected ? (theme: any) => `inset 0 0 0 2px ${theme.palette.primary.main}` : "none",
    })

    return (
        <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {rows.map(row => {
                    const color = resolveClassColor(row.expectedClass, classOptions)
                    const selected = drilldownEquals(row.expectedClass, undefined, undefined)
                    return (
                        <Chip key={row.expectedClass}
                              size="small"
                              variant="outlined"
                              onClick={() => toggleDrilldown(row.expectedClass)}
                              label={`${row.expectedClass} · ${formatPercent(row.accuracy)}`}
                              sx={{
                                  color,
                                  fontWeight: 600,
                                  borderColor: selected ? color : alpha(color, 0.5),
                                  borderWidth: selected ? 2 : 1,
                                  bgcolor: alpha(color, 0.08),
                              }}/>
                    )
                })}
            </Stack>

            <Typography variant="caption" color="text.secondary">
                Rows are the expected class, columns the predicted class. Click a cell to filter the runs below.
            </Typography>

            <TableContainer sx={{overflowX: "auto", bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2}}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell/>
                            {classes.map(predicted => (
                                <TableCell key={predicted} align="center">
                                    <Typography variant="caption" sx={headerCellSx}>
                                        <Box component="span" sx={dotSx(resolveClassColor(predicted, classOptions))}/>
                                        {predicted}
                                    </Typography>
                                </TableCell>
                            ))}
                            {hasUnmatched &&
                                <TableCell align="center">
                                    <Typography variant="caption" sx={headerCellSx}>{OTHER_COLUMN}</Typography>
                                </TableCell>
                            }
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {classes.map(expected => {
                            const row = rowByClass.get(expected)
                            return (
                                <TableRow key={expected}>
                                    <TableCell sx={{whiteSpace: "nowrap"}}>
                                        <Typography variant="caption" sx={headerCellSx}>
                                            <Box component="span" sx={dotSx(resolveClassColor(expected, classOptions))}/>
                                            {expected}
                                        </Typography>
                                    </TableCell>
                                    {classes.map(predicted => {
                                        const count = row?.predictedCounts[predicted] ?? 0
                                        return (
                                            <TableCell key={predicted}
                                                       align="center"
                                                       onClick={count > 0 ? () => toggleDrilldown(expected, predicted) : undefined}
                                                       sx={cellSx(count, expected === predicted, drilldownEquals(expected, predicted, undefined))}>
                                                {count}
                                            </TableCell>
                                        )
                                    })}
                                    {hasUnmatched &&
                                        <TableCell align="center"
                                                   onClick={(row?.unmatched ?? 0) > 0 ? () => toggleDrilldown(expected, undefined, true) : undefined}
                                                   sx={cellSx(row?.unmatched ?? 0, false, drilldownEquals(expected, undefined, true))}>
                                            {row?.unmatched ?? 0}
                                        </TableCell>
                                    }
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip size="small"
                      label="Incorrect only"
                      variant={filter.incorrectOnly ? "filled" : "outlined"}
                      onClick={() => onFilterChange({...filter, incorrectOnly: filter.incorrectOnly ? undefined : true})}/>
                <Chip size="small"
                      label={`Low confidence (<${Math.round(LOW_CONFIDENCE_THRESHOLD * 100)}%)`}
                      variant={filter.lowConfidenceOnly ? "filled" : "outlined"}
                      onClick={() => onFilterChange({...filter, lowConfidenceOnly: filter.lowConfidenceOnly ? undefined : true})}/>
                {hasActiveClassifierFilter(filter) &&
                    <Chip size="small"
                          label="Clear filters"
                          variant="outlined"
                          color="primary"
                          onClick={() => onFilterChange({})}/>
                }
            </Stack>
        </Stack>
    )
}

export default ClassifierMetricsPanel;
